import { CacheFactory, Cache } from '../../core/api/v1/client/ui/services/cache'
import type { IViewState } from '../../index'

interface ViewStateListCacheKey {
  type: 'list'
  typeName: string
  scope?: string
}

interface ViewStateDefaultCacheKey {
  type: 'default'
  typeId: string
  scope?: string
}

type ViewStateCacheKey = ViewStateListCacheKey | ViewStateDefaultCacheKey

let cacheInstance: Cache<ViewStateCacheKey, IViewState[] | IViewState> | null =
  null

/**
 * Get the view state cache instance (lazy initialization)
 * This allows tests to control cache configuration via localStorage before initialization
 */
const getViewStateCache = (): Cache<
  ViewStateCacheKey,
  IViewState[] | IViewState
> => {
  if (!cacheInstance) {
    cacheInstance = CacheFactory.create<
      ViewStateCacheKey,
      IViewState[] | IViewState
    >(
      'view-state',
      true, // enablePersistence
      true, // enableSWR
    )
  }
  return cacheInstance
}

/**
 * Reset the cache instance (primarily for testing)
 */
export const resetViewStateCacheInstance = () => {
  cacheInstance = null
}

export const viewStateCache = {
  execute: <T>(key: ViewStateCacheKey, fn: () => Promise<T>) =>
    getViewStateCache().execute(key, fn as any) as Promise<T>,
  invalidate: (key: ViewStateCacheKey) => getViewStateCache().invalidate(key),
}

/**
 * Helper to create a cache key for listing view states
 */
export const createListCacheKey = (
  typeName: string,
  scope?: string,
): ViewStateListCacheKey => ({
  type: 'list',
  typeName,
  scope,
})

/**
 * Helper to create a cache key for default view state
 */
export const createDefaultCacheKey = (
  typeId: string,
  scope?: string,
): ViewStateDefaultCacheKey => ({
  type: 'default',
  typeId,
  scope,
})

/**
 * Invalidate cache for a specific scope
 */
export const invalidateViewStateCache = (
  typeName?: string,
  typeId?: string,
  scope?: string,
) => {
  if (typeName) {
    viewStateCache.invalidate(createListCacheKey(typeName, scope))
  }
  if (typeId) {
    viewStateCache.invalidate(createDefaultCacheKey(typeId, scope))
  }
}
